/**
 * Item Library Integration for Drainage
 * Maps field names to rates from Item Library API
 * Only "Lab per plot" fields use item library
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { DRAINAGE_REFERENCES } from './drainageReferenceMapping';
import { DRAINAGE_FIELD_LABELS } from './drainageFieldMapping';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'Nr',
      resourceCode: normalizedCode,
    };
  }

  // Item not found - use fallback description if provided
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'Nr',
    resourceCode: '',
  };
}

/**
 * Create a rate lookup map from item library for Drainage
 * @param itemLibraryData - Item library response from API
 * @param groundworksState - Optional groundworks state for dynamic code resolution
 */
export function createDrainageRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  groundworksState?: any,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  const dimValues: Record<string, string> = groundworksState?.drainageDimensionValues || {};

  // NME PPIC fields: rate = average of GWALPDD018 + GWALPDD019, description fixed to "NME PPIC"
  const NME_PPIC_FIELDS = new Set([
    'osmaNmePpic', 'osmaStormNmePpic', 'polypipeNmePpic', 'polypipeStormNmePpic',
  ]);
  const nmePpicRate = (() => {
    const r018 = resourceCodeMap.get('GWALPDD018')?.nettRate ?? resourceCodeMap.get('GWALPDD018')?.baseRate ?? 0;
    const r019 = resourceCodeMap.get('GWALPDD019')?.nettRate ?? resourceCodeMap.get('GWALPDD019')?.baseRate ?? 0;
    return (r018 + r019) / 2;
  })();

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(DRAINAGE_REFERENCES).forEach(([fieldName, reference]) => {
    const fallbackDescription = DRAINAGE_FIELD_LABELS[fieldName] || fieldName;

    // NME PPIC fields: averaged rate, fixed label
    if (NME_PPIC_FIELDS.has(fieldName)) {
      rateMap.set(fieldName, { rate: nmePpicRate, description: 'NME PPIC', unit: 'Nr', resourceCode: 'GWALPDD018/GWALPDD019' });
      return;
    }

    // Dynamic code from drainageDimensionValues (e.g. land drain type selection)
    if (reference.dimensionValueKey) {
      const dynamicCode = dimValues[reference.dimensionValueKey];
      const columnHeaders: Record<string, string> = groundworksState?.drainageColumnHeaders || {};
      const dynamicLabel = columnHeaders[fieldName] || fallbackDescription;
      if (dynamicCode) {
        const rateInfo = getRateByCode(dynamicCode, resourceCodeMap, fieldName, fallbackDescription);
        rateMap.set(fieldName, { ...rateInfo, description: dynamicLabel });
      } else {
        rateMap.set(fieldName, { rate: 0, description: dynamicLabel, unit: 'm', resourceCode: '' });
      }
      return;
    }

    if (!reference.code) {
      rateMap.set(fieldName, { rate: 0, description: fallbackDescription, unit: 'Nr', resourceCode: '' });
      return;
    }

    // Static code
    rateMap.set(fieldName, getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription));
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getDrainageRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: 'Nr' };
}

/**
 * Apply rates and descriptions to Drainage summary items
 */
export function applyDrainageRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getDrainageRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
